<template>
  <div class="column content-box">
    <woot-modal-header :header-title="$t('CAMPAIGN.ADD.TITLE')" />

    <timeline :current_page="page" :total="totalPages" />

    <form class="row" @submit.prevent="addCampaign">
      <div class="medium-12 columns">
        <div v-if="page === 1">          
          <woot-input
            v-model="title"
            :label="$t('CAMPAIGN.ADD.FORM.TITLE.LABEL')"
            type="text"
            :class="{ error: $v.title.$error }"
            :error="$v.title.$error ? $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') : ''"
            :placeholder="$t('CAMPAIGN.ADD.FORM.TITLE.PLACEHOLDER')"
            @blur="$v.title.$touch"
          />
          <div class="row" v-if="isAccountTypeParent">            
            <div class="medium-12" :class="{ error: $v.selectedSubAccount.$error }">
              <label>{{ $t('CAMPAIGN.ADD.FORM.SUBACCOUNT.LABEL') }}</label>
              <multiselect
                v-model="selectedSubAccount"
                :options="subaccounts"
                :placeholder="$t('CAMPAIGN.ADD.FORM.MULTISELECT.PLACEHOLDER')"
                track-by="id"
                label="name"
                class="multiselect-wrap--small"
                @select="onChangeSubAccount"
                @remove="onChangeSubAccount(null)"
              >
                <template #noResult>
                  <span>{{ $t('CAMPAIGN.ADD.FORM.MULTISELECT.NO_RESULTS') }}</span>
                </template>
              </multiselect>
              <span v-if="$v.selectedSubAccount.$error" class="message">
                {{ $t('CAMPAIGN.ADD.FORM.SUBACCOUNT.ERROR') }}
              </span>
            </div>            
          </div>
          <div v-if="subaccountSelectsLoading" class="subaccount-data-selects-loading">            
            <spinner :colorScheme="'dark'" />
            <span>{{ $t('CAMPAIGN.ADD.FORM.SUBACCOUNT.FETCHING_INFO_SELECTS') }}</span>
          </div>
          <div v-if="!isAccountTypeParent || (selectedSubAccount && !subaccountSelectsLoading)">
            <div class="row">            
              <div class="medium-12" :class="{ error: $v.selectedInbox.$error }">
                <label>{{ $t('CAMPAIGN.ADD.FORM.INBOX.LABEL') }}</label>
                <multiselect
                  v-model="selectedInbox"
                  :options="inboxes"
                  :placeholder="$t('CAMPAIGN.ADD.FORM.MULTISELECT.PLACEHOLDER')"
                  track-by="id"
                  label="name"
                  class="multiselect-wrap--small"
                  @select="onChangeInbox"
                  @remove="onChangeInbox(null)"
                >
                  <template #noResult>
                    <span>{{ $t('CAMPAIGN.ADD.FORM.MULTISELECT.NO_RESULTS') }}</span>
                  </template>
                </multiselect>
                <span v-if="$v.selectedInbox.$error" class="message">
                  {{ $t('CAMPAIGN.ADD.FORM.INBOX.ERROR') }}
                </span>
              </div>
            </div>
            <div class="audience-type-container">
              <div class="audience-type-title">
                {{ $t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.LABEL') }}
              </div>

              <div class="audience-type-buttons">
                <div
                  class="audience-type-option"
                  :class="{
                    selected:
                      selectedTypeAudience && selectedTypeAudience.id === 'label',
                  }"
                  @click="selectAudienceType('label')"
                >
                  <div class="icon">
                    <woot-button
                      variant="clear"
                      size="small"
                      color-scheme="secondary"
                      icon="tag"
                      class="audience-icon"
                    />
                  </div>
                  <div class="text">
                    {{ $t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.TYPES.LABEL') }}
                  </div>
                </div>

                <div
                  class="audience-type-option"
                  :class="{
                    selected:
                      selectedTypeAudience && selectedTypeAudience.id === 'file',
                  }"
                  @click="selectAudienceType('file')"
                >
                  <div class="icon">
                    <woot-button
                      variant="clear"
                      size="small"
                      color-scheme="secondary"
                      icon="file-upload"
                      class="audience-icon"
                    />
                  </div>
                  <div class="text">
                    {{ $t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.TYPES.FILE') }}
                  </div>
                </div>
              </div>

              <span v-if="$v.selectedTypeAudience.$error" class="message">
                {{ $t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.ERROR') }}
              </span>
            </div>

            <label
              v-if="selectedTypeAudience && selectedTypeAudience.id === 'label'"
              class="multiselect-wrap--small"
              :class="{ error: $v.selectedAudience.$error }"
            >
              {{ $t('CAMPAIGN.ADD.FORM.AUDIENCE.LABEL') }}
              <multiselect
                v-model="selectedAudience"
                :options="audienceList"
                track-by="id"
                label="title"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="$t('CAMPAIGN.ADD.FORM.AUDIENCE.PLACEHOLDER')"
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                @blur="$v.selectedAudience.$touch"
                @select="$v.selectedAudience.$touch"
              />
              <span v-if="$v.selectedAudience.$error" class="message">
                {{ $t('CAMPAIGN.ADD.FORM.AUDIENCE.ERROR') }}
              </span>
            </label>          

            <div v-if="selectedTypeAudience && selectedTypeAudience.id === 'file'">
              <label>{{ $t('CAMPAIGN.ADD.FORM.DDI.LABEL') }} <small>{{ $t('CAMPAIGN.ADD.FORM.DDI.COMPLEMENT_LABEL') }}</small></label>
              <multiselect 
                v-model="selectedCountry"
                :options="countries"
                :custom-label="countryOptionLabel"
                :searchable="true"
                label="name"
                track-by="id">             
              </multiselect>
              <label            
                class="multiselect-wrap--small"
                :class="{ error: $v.importFile.$error }"
              >
                {{ $t('CAMPAIGN.ADD.FORM.IMPORT_FILE.LABEL') }}
                <input
                  id="file"
                  ref="file"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  :disabled="uiFlags.isAnalyzingFile && !showTimeOutFileAnalysis"
                  @change="handleFileUpload"
                />
                <span v-if="$v.importFile.$error" class="message">
                  {{ $t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ERROR') }}
                </span>
              </label>
              <template v-if="importFile">
                <div class="file-import-analysis" v-if="uiFlags.isAnalyzingFile || canShowFileAnalysis || showTimeOutFileAnalysis">
                  <p v-if="showTimeOutFileAnalysis">
                    {{ $t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.TIMEOUT') }}
                  </p>
                  <template v-else-if="canShowFileAnalysis">                  
                    {{ $t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.LABEL') }}
                    <ul v-for="(message, index) in messagesFileAnalysis" :key="`${index}_message`">
                      <li v-dompurify-html="formatMessage(message)"></li>
                    </ul>
                  </template>
                  <span v-else-if="uiFlags.isAnalyzingFile">
                    <spinner :colorScheme="'dark'" /> {{ $t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.SUBMITTING') }}
                  </span>
                </div>
              </template>
            </div>
            <div class="row">            
              <div class="medium-12">
                <label>{{ $t('CAMPAIGN.ADD.FORM.TEAM.LABEL') }}</label>
                <multiselect
                  v-model="selectedTeam"
                  :options="teams"
                  :placeholder="$t('CAMPAIGN.ADD.FORM.MULTISELECT.PLACEHOLDER')"
                  track-by="id"
                  label="name"
                  class="multiselect-wrap--small"                
                >
                  <template #noResult>
                    <span>{{ $t('CAMPAIGN.ADD.FORM.MULTISELECT.NO_RESULTS') }}</span>
                  </template>
                </multiselect>
              </div>
              <div class="medium-12">
                <label>{{ $t('CAMPAIGN.ADD.FORM.TAG.LABEL') }}</label>
                <multiselect
                  v-model="selectedTag"
                  :options="audienceList"
                  :placeholder="$t('CAMPAIGN.ADD.FORM.MULTISELECT.PLACEHOLDER')"
                  track-by="id"
                  label="title"
                  class="multiselect-wrap--small"                
                >
                  <template #noResult>
                    <span>{{ $t('CAMPAIGN.ADD.FORM.MULTISELECT.NO_RESULTS') }}</span>
                  </template>
                </multiselect>
              </div>            
            </div>            
          </div>
        </div>
        <div v-if="page === 2">
          <div class="card-option-container">
            <div class="card-option-title">
              {{ $t('CAMPAIGN.ADD.FORM.STATUS.LABEL') }}
            </div>

            <div class="card-option-buttons">
              <div
                class="card-option-option"
                :class="{ selected: selectedStatus === 'snoozed' }"
                @click="selectStatus('snoozed')"
              >
                <div class="icon">
                  <woot-button
                    variant="clear"
                    size="small"
                    color-scheme="secondary"
                    icon="arrow-right"
                    class="status-icon"
                  />
                </div>
                <div class="text">
                  {{ $t('CAMPAIGN.ADD.FORM.STATUS.PENDING') }}
                </div>
              </div>
              <div
                class="card-option-option"
                :class="{ selected: selectedStatus === 'open' }"
                @click="selectStatus('open')"
              >
                <div class="icon">
                  <woot-button
                    variant="clear"
                    size="small"
                    color-scheme="secondary"
                    icon="lock-closed"
                    class="status-icon"
                  />
                </div>
                <div class="text">
                  {{ $t('CAMPAIGN.ADD.FORM.STATUS.OPEN') }}
                </div>
              </div>
              <div
                class="card-option-option"
                :class="{ selected: selectedStatus === 'resolved' }"
                @click="selectStatus('resolved')"
              >
                <div class="icon">
                  <woot-button
                    variant="clear"
                    size="small"
                    color-scheme="secondary"
                    icon="checkmark-double"
                    class="status-icon"
                  />
                </div>
                <div class="text">
                  {{ $t('CAMPAIGN.ADD.FORM.STATUS.RESOLVED') }}
                </div>
              </div>
            </div>
            <div class="option-description">
              <p
                v-html="$t('CAMPAIGN.ADD.FORM.STATUS.DESCRIPTION.PENDING')"
              ></p>
              <p v-html="$t('CAMPAIGN.ADD.FORM.STATUS.DESCRIPTION.OPEN')"></p>
              <p
                v-html="$t('CAMPAIGN.ADD.FORM.STATUS.DESCRIPTION.RESOLVED')"
              ></p>
              <p v-html="$t('CAMPAIGN.ADD.FORM.STATUS.DESCRIPTION.FOOTER')"></p>
            </div>
          </div>
        </div>
        <div v-if="page === 3">
          <div class="card-option-container">
            <div class="card-option-title">
              {{ $t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.LABEL') }}
            </div>

            <div class="card-option-buttons">
              <div
                 v-for="(actionToOpenConversationOption, index) in actionToOpenConversation.options" :key="`${index}_action_to_open_conversation_option`"
                class="card-option-option"
                :class="{ selected: actionToOpenConversation.selected === actionToOpenConversationOption.value }"
                @click="selectActionToOpenConversationOption(actionToOpenConversationOption.value)"
              >
                <div class="icon">
                  <woot-button
                    variant="clear"
                    size="small"
                    color-scheme="secondary"
                    :icon="actionToOpenConversationOption.icon"
                    class="status-icon"
                  />
                </div>
                <div class="text">{{ actionToOpenConversationOption.label }}</div>
              </div>              
            </div>
            <div class="option-description">
              <p
                v-for="(actionToOpenConversationOption, index) in actionToOpenConversation.options" :key="`${index}_action_to_open_conversation_description`"
                v-html="actionToOpenConversationOption.description"
              ></p>
              
            </div>
            <label class="error" v-if="$v.actionToOpenConversation.selected.$error" >                
                <span class="message">
                  {{ $t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.ERROR') }}
                </span>
            </label>
          </div>
          <alert
            :show="actionToOpenConversation.selected == 'execute_all' && selectedStatus == 'resolved'"
            :content="$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.ALERT_RESOLVED_AND_EXECUTE_ALL')"
            type="warning"
            icon="warning"
            iconSize="20"
          />
        </div>
        <div v-if="page === 4">
          <label :class="{ error: $v.selectedTemplate.$error }">
            {{ $t('CAMPAIGN.ADD.FORM.TEMPLATE.LABEL') }}

            <templates-picker
              v-if="!selectedTemplate"
              :inbox-id="selectedInbox.id"
              :template-filter="{ authentication: false }"
              @onSelect="pickTemplate"
            />

            <span v-if="$v.selectedTemplate.$error" class="message">
              {{ $t('CAMPAIGN.ADD.FORM.TEMPLATE.ERROR') }}
            </span>
          </label>

          <label :class="{ error: $v.attachedFiles.$error }">
            <span v-if="$v.attachedFiles.$error" class="message custom-error">
              {{ $t('CAMPAIGN.ADD.FORM.FILE.ERROR') }}
            </span>
          </label>

          <template-parser
            v-if="selectedTemplate"
            type="campaign"
            :template="selectedTemplate"
            :enable-parameterization="isEnableParameterization"
            :inbox-id="selectedInbox.id"
            @resetTemplate="onResetTemplate"
            @sendMessage="getEventTemplate"
          />
        </div>

        <div v-if="page === totalPages">
          <div class="row">
            <div class="medium-12" :class="{ error: $v.selectedSender.$error }">
              <label>{{ $t('CAMPAIGN.ADD.FORM.SENT_BY.LABEL') }}</label>
              <multiselect
                v-model="selectedSender"
                :options="senderList"
                :placeholder="$t('CAMPAIGN.ADD.FORM.MULTISELECT.PLACEHOLDER')"
                :disabled="isWhatsappType && !selectedInbox"
                track-by="id"
                label="name"
                class="multiselect-wrap--small"
              >
                <template #noResult>
                  <span>{{ $t('CAMPAIGN.ADD.FORM.MULTISELECT.NO_RESULTS') }}</span>
                </template>
              </multiselect>
              <span v-if="$v.selectedSender.$error" class="message">
                {{ $t('CAMPAIGN.ADD.FORM.SENT_BY.ERROR') }}
              </span>
            </div>
            <div class="medium-12">
                <label>
                  {{ $t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.LABEL') }}
                  <woot-date-time-picker
                    :value="scheduledAt"
                    :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                    :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                    @change="onChange"
                  />
                </label>
            </div>
          </div>          
        </div>
      </div>

      <div class="modal-footer">
        <woot-button
          @click.prevent="changePage('minus')"
          :isDisabled="canDisableButtonBack"
        >
          {{ $t('GENERAL_SETTINGS.BACK') }}
        </woot-button>

        <div v-if="!(page === 4 && selectedTemplate)">
          <woot-button v-if="canChangePage"
            @click.prevent="changePage('add')"
            :isDisabled="canDisableButtonNext"

          >
            {{ $t('CAMPAIGN.NEXT.BUTTON_TEXT') }}
          </woot-button>

          <woot-button
            v-else
            :is-loading="uiFlags.isCreating || isSubmitting || (uiFlags.isAnalyzingFile && !showTimeOutFileAnalysis)"
          >
            {{ $t('CAMPAIGN.ADD.CREATE_BUTTON_TEXT') }}
          </woot-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FileUpload from 'vue-upload-component';

import { CAMPAIGNS_EVENTS } from '../../../../../helper/AnalyticsHelper/events';

import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker';
import TemplateParser from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplateParser';
import Timeline from 'shared/components/Timeline';
import Spinner from 'shared/components/Spinner.vue';
import Alert from 'shared/components/Alert';

import alertMixin from 'shared/mixins/alertMixin';
import campaignMixin from 'shared/mixins/campaignMixin';
import snoozeTimesMixin from 'dashboard/mixins/conversation/snoozeTimesMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import countries from 'shared/constants/countries.js';

import InboxesAPI from '../../../../../api/inboxes.js';
import TeamsAPI from '../../../../../api/teams.js';
import LabelsAPI from '../../../../../api/labels.js';


export default {
  components: {
    AttachmentPreview,
    FileUpload,
    TemplatesPicker,
    TemplateParser,
    Timeline,
    WootDateTimePicker,
    Spinner,
    Alert
  },
  mixins: [alertMixin, campaignMixin, snoozeTimesMixin, messageFormatterMixin],
  data() {
    return {
      title: '',
      message: '',
      selectedSender: null,
      selectedInbox: null,
      selectedTypeAudience: null,
      selectedAudience: [],
      selectedTemplate: null,
      selectedTeam: null,
      selectedTag: null,
      hasParameters: false,
      scheduledAt: null,
      senderList: [],
      templateParams: null,
      attachedFiles: null,
      importFile: null,
      page: 1,
      totalPages: 5,
      typeAudienceList: [
        {
          id: 'label',
          title: this.$t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.TYPES.LABEL'),
        },
        {
          id: 'file',
          title: this.$t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.TYPES.FILE'),
        },
      ],
      selectedStatus: 'snoozed',
      isSubmitting: false,
      inboxLockToSingleConversation: false,
      countries: countries,
      selectedCountry: countries.find((c) => c.id == 'BR') || null,
      intervalAwaitFileAnalysis: null,
      showTimeOutFileAnalysis: false,
      unprocessableErrorMessage: null,
      selectedSubAccount: null,
      subaccountSelectsLoading: false,
      subaccountSelects: {
        inboxes: [],
        teams: [],
        audienceList: []
      },      
      actionToOpenConversation: {
        selected: 'skip',
        options: [
          {
            value: 'skip',
            label: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.SKIP'),
            description: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.DESCRIPTION.SKIP'),
            icon: 'dismiss'
          },
          {
            value: 'only_messages',
            label: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.ONLY_MESSAGES'),
            description: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.DESCRIPTION.ONLY_MESSAGES'),
            icon: 'checkmark'
          },
          {
            value: 'execute_all',
            label: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.EXECUTE_ALL'),
            description: this.$t('CAMPAIGN.ADD.FORM.ACTION_TO_OPEN_CONVERSATION.DESCRIPTION.EXECUTE_ALL'),
            icon: 'checkmark-double'
          }
        ]
      }
    };
  },
  validations() {
    return {
      title: { required },
      message: { required },
      selectedInbox: { required },
      selectedSender: { required },
      selectedTemplate: { required },
      selectedStatus: { required },
      selectedTypeAudience: { required },
      attachedFiles: {
        required: requiredIf(function () {
          return (
            this.formatTypeTemplateHeader &&
            this.formatTypeTemplateHeader !== 'TEXT'
          );
        }),
      },
      selectedAudience: {
        required: requiredIf(function () {
          return (
            this.selectedTypeAudience &&
            this.selectedTypeAudience.id === 'label'
          );
        }),
      },
      importFile: {
        required: requiredIf(function () {
          return (
            this.selectedTypeAudience && this.selectedTypeAudience.id === 'file'
          );
        }),
      },
      selectedSubAccount: {
        required: requiredIf(function () {
          return this.isAccountTypeParent;
        }),
      },
      actionToOpenConversation: { 
        selected: {required}
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',      
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',      
      fileAnalysis: 'campaigns/getFileAnalysis',
      isAccountTypeParent: 'isAccountTypeParent',
      subaccounts: 'subaccounts/getSubaccounts',
    }),
    inboxes() {
      if(this.isAccountTypeParent){
        return this.subaccountSelects.inboxes;
      }
      return this.$store.getters['inboxes/getWhatsAppInboxes'];
    },
    teams(){
      if(this.isAccountTypeParent){
        return this.subaccountSelects.teams;
      }
      return this.$store.getters['teams/getTeams'];
    },
    audienceList() {
      if(this.isAccountTypeParent){
        return this.subaccountSelects.audienceList;
      }
      return this.$store.getters['labels/getLabels'];
    },
    conversationStatuses() {
      return [
        {
          id: 1,
          key: 'open',
          value: this.$t('CONVERSATION.CARD_CONTEXT_MENU.OPEN'),
        },
        {
          id: 2,
          key: 'resolved',
          value: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
        },
        {
          id: 3,
          key: 'pending',
          value: this.$t('CONVERSATION.CARD_CONTEXT_MENU.PENDING'),
        },
        {
          id: 4,
          key: 'snoozed',
          value: `
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.TITLE')}
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.TOMORROW')}
          `,
          snooze: this.snoozeTimes.tomorrow,
        },
        {
          id: 5,
          key: 'snoozed',
          value: `
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.TITLE')}
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.NEXT_WEEK')}
          `,
          snooze: this.snoozeTimes.nextWeek,
        },
        {
          id: 6,
          key: 'snoozed',
          value: `
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.TITLE')}
            ${this.$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE.NEXT_REPLY')}
          `,
        },
      ];
    },
    canChangePage() {
      return this.page < this.totalPages;
    },
    canDisableButtonBack() {
      return this.page === 1 || (this.page === this.totalPages && this.uiFlags.isCreating);
    },
    canDisableButtonNext() {
      if(this.page != 1 || !this.selectedTypeAudience || this.selectedTypeAudience.id != 'file' || !this.importFile){
        return false;
      }
      if(this.showTimeOutFileAnalysis) return false;
      return !(!this.uiFlags.isAnalyzingFile && (!this.canShowFileAnalysis || this.fileAnalysis?.countValid > 0));
    },
    formatTypeTemplateHeader() {
      if (this.selectedTemplate) {
        const [header] = this.selectedTemplate.components.filter(({ type }) => {
          return type == 'HEADER';
        });

        return header?.format;
      }
    },
    getParams() {
      window.history.pushState({}, null, `${this.$route.path}?page=1`);

      return { page: 1, sortAttr: '-created_at', search: '' };
    },
    isEnableParameterization() {
      return this.$route.name === 'whatsapp';
    },
    isWhatsappType() {
      return this.campaignType === 'whatsapp';
    },    
    canShowFileAnalysis(){
      return this.messagesFileAnalysis.length > 0;
    },
    messagesFileAnalysis(){
      if(this.importFile && !this.uiFlags.isAnalyzingFile){
        let messages = [];
        if(this.fileAnalysis === null || this.unprocessableErrorMessage){
          if(this.unprocessableErrorMessage){
            messages.push(this.unprocessableErrorMessage);
          }else{
            messages.push(this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.ERROR'));
          }
        }else{
          if(this.fileAnalysis?.countValid == 0){            
            messages.push(this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.NO_VALID_ROWS'));
          }
          let count;
          if(this.fileAnalysis?.countWithoutName > 0){
            count = this.fileAnalysis.countWithoutName;
            messages.push(this.$t(`CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.WITHOUTNAME${count > 1 ? 'S' : ''}`, { count }));
          }
          if(this.fileAnalysis?.countWithoutPhone > 0){
            count = this.fileAnalysis.countWithoutPhone;
            messages.push(this.$t(`CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.WITHOUTPHONE${count > 1 ? 'S' : ''}`, { count }));              
          }
          if(this.fileAnalysis?.countWithInvalidPhone > 0){
            count = this.fileAnalysis.countWithInvalidPhone;
            messages.push(this.$t(`CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.WITHINVALIDPHONE${count > 1 ? 'S' : ''}`, { count }));
          }            
          if(this.fileAnalysis?.countWithDuplicatePhone > 0){
            messages.push(this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.WITHDUPLICATEPHONE', { count: this.fileAnalysis.countWithDuplicatePhone }));
          }
          
        }
        return messages;
      }
      return [];
    }
  },
  watch: {
    'uiFlags.isAnalyzingFile'(newValue, oldValue) {
      if(oldValue && !newValue){
        this.clearIntervalAwaitFileAnalysis();
      }      
    },
  },
  mounted() {
    this.$track(CAMPAIGNS_EVENTS.OPEN_NEW_CAMPAIGN_MODAL, {
      type: this.campaignType,
    });
    if (!this.selectedTypeAudience) {
      this.selectedTypeAudience = this.typeAudienceList[0];
    }
    if(this.isAccountTypeParent && this.subaccounts.length == 0){
      this.$store.dispatch('subaccounts/get');
    }
  },
  beforeDestroy() {
    this.clearIntervalAwaitFileAnalysis();
    if(this.isAccountTypeParent){
      this.$store.dispatch('inboxes/get');
    }
  },
  methods: {
    async onChangeInbox(inbox) {      
      try {
        this.inboxLockToSingleConversation = null
        if(!inbox) {
          this.senderList = [];
          this.selectedTeam = null;
          return;
        }
        const selectedInbox = inbox.id;
        let params = { inboxId: selectedInbox };
        if(this.isAccountTypeParent && this.selectedSubAccount){
          params.accountId = this.selectedSubAccount.id;
        }
        const response = await this.$store.dispatch('inboxMembers/get', params);
        const {
          data: { payload: inboxMembers },
        } = response;
        inboxMembers.sort((a, b) => a.name.localeCompare(b.name));        
        this.senderList = inboxMembers;
        this.inboxLockToSingleConversation = this.inboxes.find(inbox => inbox.id === selectedInbox)?.lock_to_single_conversation || false;
        if(!this.inboxLockToSingleConversation && this.keepSendingToActiveConversations){
          this.keepSendingToActiveConversations = false;
        }
      } catch (error) {
        const errorMessage =
          error?.response?.message || this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    async onChangeSubAccount(subaccount){
      if(!subaccount){
        return;
      }
      try{
        this.subaccountSelectsLoading = true;
        const responseInboxes = await InboxesAPI.getByAccount(subaccount.id);
        this.subaccountSelects.inboxes = responseInboxes.data.payload ? responseInboxes.data.payload : responseInboxes.data;

        const responseTeams = await TeamsAPI.getByAccount(subaccount.id);
        this.subaccountSelects.teams = responseTeams.data.payload ? responseTeams.data.payload : responseTeams.data;

        const responseLabels = await LabelsAPI.getByAccount(subaccount.id);
        this.subaccountSelects.audienceList = responseLabels.data.payload ? responseLabels.data.payload : responseLabels.data;

        this.$store.dispatch('inboxes/setInboxes', this.subaccountSelects.inboxes);
        this.subaccountSelectsLoading = false;
      }catch(error){
        this.subaccountSelectsLoading = false;
        this.selectedSubAccount = null;
        this.showAlert(this.$t('CAMPAIGN.ADD.FORM.SUBACCOUNT.ERROR_FETCH_INFO_SELECTS'));

      }
    },
    async addCampaign() {
      if (this.isSubmitting) return;

      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isSubmitting = true;

      try {
        const campaignDetails = this.getCampaignDetails();

        if (
          this.selectedTypeAudience &&
          this.selectedTypeAudience.id === 'file' &&
          this.importFile
        ) {
          await this.$store.dispatch(
            'campaigns/createFormData',
            campaignDetails
          );
        } else {
          await this.$store.dispatch('campaigns/create', campaignDetails);
        }

        await this.$store.dispatch(
          'campaigns/getCampaignWhatsapp',
          this.getParams
        );

        this.$track(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
          type: this.campaignType,
        });

        this.showAlert(this.$t('CAMPAIGN.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.message || this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);

        this.isSubmitting = false;
      }
    },
    onClose() {
      this.$emit('on-close');
    },
    onChange(value) {
      this.scheduledAt = value;
    },
    pickTemplate(template) {
      this.selectedTemplate = template;
    },
    onResetTemplate() {
      this.$emit('pickTemplate', false);
      this.selectedTemplate = null;
    },
    getEventTemplate(event) {
      if (event.files) {
        this.attachedFiles = event.attachedFile;
      }

      if (!event.files) {
        const { message, templateParams, hasParameters } = event;

        this.message = message;
        this.templateParams = templateParams;
        this.hasParameters = hasParameters;
      }

      this.changePage('add');
    },
    getCampaignDetails() {
      const audience = this.selectedAudience.map(item => {
        return {
          id: item.id,
          type: 'Label',
        };
      });

      const status =
        this.conversationStatuses.find(s => {
          return s.id == this.selectedStatus || s.key === this.selectedStatus;
        }) || null;

      let campaignDetails = {
        title: this.title,
        message: this.message,
        inbox_id: this.selectedInbox.id,
        scheduled_at: this.scheduledAt,
        sender_id: this.selectedSender ? this.selectedSender.id : null,
        team_id: this.selectedTeam ? this.selectedTeam.id: null,
        tag_id: this.selectedTag ? this.selectedTag.id : null,
        has_parameters: this.hasParameters,
        audience,
        audience_type: this.selectedTypeAudience
          ? this.selectedTypeAudience.id
          : null,
        trigger_rules: {
          status: status ? status.key : null,
          template_params: this.templateParams          
        },
        account_id: this.isAccountTypeParent && this.selectedSubAccount ? this.selectedSubAccount.id : null,
        action_to_open_conversation: this.actionToOpenConversation.selected
      };

      if (this.attachedFiles) {
        campaignDetails.file = this.attachedFiles;
      }

      if (
        this.selectedTypeAudience &&
        this.selectedTypeAudience.id == 'file' &&
        this.importFile
      ) {
        campaignDetails.import_file = this.importFile;
        campaignDetails.trigger_rules.country_ddi = this.selectedCountry?.dial_code || null;
      }

      return campaignDetails;
    },
    changePage(type) {
      const { page } = this;

      if (type === 'minus' && page > 1) {
        if (page === 4) {
          this.selectedTemplate = null;
          this.attachedFiles = null;
          this.$v.$reset();
        }

        --this.page;
        return;
      }

      if (type === 'add' && page < this.totalPages) {
        this.$v.$touch();

        if (
          (page === 1 &&
            (this.$v.title.$error ||
              this.$v.selectedInbox.$error ||
              this.$v.selectedTypeAudience.$error ||
              (this.selectedTypeAudience &&
                this.selectedTypeAudience.id === 'label' &&
                this.$v.selectedAudience.$error) ||
              (this.selectedTypeAudience &&
                this.selectedTypeAudience.id === 'file' &&
                this.$v.importFile.$error))) ||
          (page === 2 && this.$v.selectedStatus.$error) ||
          (page === 3 && this.$v.actionToOpenConversation.selected.$error) ||
          (page === 4 &&
            (this.$v.selectedTemplate.$error || this.$v.attachedFiles.$error))
        ) {
          return;
        }

        this.$v.$reset();

        ++this.page;
      }
    },
    async handleFileUpload() {
      if(this.$refs.file.files[0].type != 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'){
        this.importFile = null;
        this.$v.importFile.$touch();
        return;
      }
      this.importFile = this.$refs.file.files[0];
      
      this.showTimeOutFileAnalysis = false;
      this.unprocessableErrorMessage = null;
      try {
        await this.$store.dispatch(
          'campaigns/fileAnalysis',
          { file: this.importFile, countryDDI: this.selectedCountry?.dial_code }
        );
        this.intervalAwaitFileAnalysis = setTimeout(() => {          
          this.showTimeOutFileAnalysis = true;
          this.$store.dispatch('campaigns/updateUiFlag', { flag: 'isAnalyzingFile', value: false });
        }, 60000)
      } catch (error) {
        if (error?.response?.status === 422) {
          let message = error?.response?.data?.error;
          if(message){
            if(message.includes('Invalid file')){
              message = this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.INVALID_FILE_FORMAT');
            }else if(message.includes('contain a phone')){
              message = this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.ANALYSIS.MESSAGES.NO_VALID_ROWS');
            }
          }
          this.unprocessableErrorMessage = message;
        }        
      }      
    },
    selectAudienceType(type) {
      this.selectedTypeAudience = this.typeAudienceList.find(
        t => t.id === type
      );
      this.$v.selectedTypeAudience.$touch();      
    },
    selectStatus(status) {
      this.selectedStatus = status;
      this.$v.selectedStatus.$touch();
    },
    countryOptionLabel({emoji, name, dial_code}){
      return `${emoji} ${name} (${dial_code})`;
    },
    clearIntervalAwaitFileAnalysis() {
      if (this.intervalAwaitFileAnalysis) {
        clearTimeout(this.intervalAwaitFileAnalysis);
        this.intervalAwaitFileAnalysis = null;
        this.$store.dispatch('campaigns/updateUiFlag', { flag: 'isAnalyzingFile', value: false });
      }
    },
    selectActionToOpenConversationOption(value){
      this.actionToOpenConversation.selected = value;
      this.$v.actionToOpenConversation.selected.$touch();
    }
  },
};
</script>

<style lang="scss" scoped>
.message {
  margin-top: var(--space-micro);

  &.custom-error {
    position: absolute;
    margin-top: var(--space-jumbo);
    left: var(--space-large);
    width: 50%;
  }
}

.modal-footer {
  button {
    margin-left: 0 !important;
  }

  width: 100%;
  justify-content: space-between !important;
}

.audience-type-container {
  margin-bottom: var(--space-mini, 12px);
}

.audience-type-title {
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--s-700);
  display: block;
  margin-bottom: var(--space-micro, 8px);
}

.audience-type-buttons {
  display: flex;
  flex-direction: row;
  gap: var(--space-two, 16px);
  margin-top: var(--space-mini, 12px);
}

.audience-type-option {
  width: 150px;
  height: 150px;
  background: var(--b-200);
  border-radius: var(--space-mini, 12px);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 0.25s;

  & .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--space-mini, 12px);

    & ::v-deep svg {
      width: 96px !important;
      height: 96px !important;
    }
  }

  & .text {
    font-size: var(--font-size-default);
    opacity: 0.9;
    color: var(--b-900);
  }
}

.audience-type-option.selected {
  background: var(--w-700);

  & .text {
    color: var(--white);
  }

  & .icon ::v-deep svg {
    fill: var(--white) !important;
    color: var(--white) !important;
  }
}

.audience-icon {
  padding: 0 !important;
  margin: 0 !important;

  &.woot-button {
    background: transparent !important;
  }
}

.card-option-container {
  margin-top: var(--space-large, 24px);
  margin-bottom: var(--space-medium, 20px);
}

.card-option-title {
  font-size: var(--font-size-small);
  font-weight: 600;
  color: var(--s-700);
  margin-bottom: var(--space-micro, 8px);
}

.card-option-buttons {
  display: flex;
  flex-direction: row;
  gap: var(--space-two, 16px);
  margin-top: var(--space-mini, 12px);
}

.card-option-option {
  width: 150px;
  height: 150px;
  background: var(--b-200);
  border-radius: var(--space-mini, 12px);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 0.25s;
  padding: 5px;
  text-align: center;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--space-mini, 12px);

    ::v-deep svg {
      width: 96px !important;
      height: 96px !important;
    }
  }

  .text {
    font-size: var(--font-size-default);
    opacity: 0.9;
    color: var(--b-900);
  }
  &.selected{
    background: var(--w-700);
    cursor: default;

    .text {
      color: var(--white);
    }

    .icon {
      ::v-deep svg {
        fill: var(--white) !important;
        color: var(--white) !important;
      }
    }

  }
}

.option-description {
  margin-top: var(--space-mini, 18px);
  font-size: var(--font-size-small);
  color: var(--s-700);
  line-height: 1.4;
  max-width: 480px;
}

.option-description p {
  margin-bottom: var(--space-mini, 10px);
}

label.disabled{
  cursor: not-allowed;
  opacity: 0.5;
}
.file-import-analysis{
  font-size: var(--font-size-small);
  padding: var(--space-normal);
  margin-bottom: var(--space-normal);
  background: var(--b-50);
  
}
.subaccount-data-selects-loading {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-normal);
  border: 1px dashed var(--color-border);
  border-radius: var(--border-radius-normal);
  background-color: var(--color-background);

  span {
    font-size: var(--font-size-small);
    color: var(--s-600);
  }
}

</style>
